import { supabase } from "@/lib/supabase";
import BaseService from "@/services/BaseService.ts";
import {CryptoHolding, CryptoWallet} from "@/integrations/supabase/crypto.models.ts";

/**
 * Service class for handling REIT-related operations, including fetching dividend payments.
 * This class provides methods to interact with the Supabase backend for REIT data.
 *
 * @class CryptoHoldingsService
 * @example
 * const reitService = new CryptoService();
 * const payments = await reitService.getReitDividendPayments(userId);
 */
export class CryptoHoldingsService extends BaseService {

  /**
   * Create crypto holding for the user
   * @param {CryptoHoldingCreateRequest} request request to send
   */
  public async createCryptoHolding(request: CryptoHoldingCreateRequest): Promise<CryptoHolding> {
    try {
      const payload = this.convertRequestToSnakeCase(request);
      const { data, error } = await supabase
          .from('crypto_holdings')
          .insert(payload)
          .select()
          .single();
      if (error) throw error;
      return data as CryptoHolding;
    } catch (error) {
      console.error("Error creating crypto holding:", error);
      await this.handleError(error);
    }
  }

  /**
   * Updates a Crypto Holding
   * @param {CryptoHoldingUpdateRequest} request update request
   */
  public async updateCryptoHolding(
    request: CryptoHoldingUpdateRequest,
  ): Promise<CryptoHolding> {
    try {
      const payload = this.convertRequestToSnakeCase(request);
      const { data, error } = await supabase
          .from('crypto_holdings')
          .update(payload)
          .eq('id', request.id)
          .select()
          .single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error updating crypto holding:", error);
      await this.handleError(error);
    }
  }

  /**
   * Deletes a crypto Holding
   * @param {string} id crypto holding ID
   */
  public async deleteCryptoHolding(
    id: string,
  ): Promise<void> {
    try {
      const { error } = await supabase
          .from('crypto_holdings')
          .delete()
          .eq('id', id);
      if (error) throw error;
    } catch (error) {
      console.error("Error deleting crypto holding:", error);
      await this.handleError(error);
    }
  }

  /**
   * Fetch crypto holdings
   */
  public async getCryptoHoldings(): Promise<CryptoHolding[]> {
    try {
      const { data, error } = await supabase
          .from('crypto_holdings')
          .select('*, wallet:crypto_wallets(*)')
          .order('symbol', { ascending: true });
      if (error) throw error;
      return data as (CryptoHolding & { wallet: CryptoWallet })[];
    } catch (error) {
      console.error("Error fetching crypto holdings", error);
      await this.handleError(error);
    }
  }
}

export default new CryptoHoldingsService();
